async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  if (res.status === 401) {
    // Redirect to login, preserve return path
    if (typeof window !== 'undefined') {
      const returnTo = window.location.pathname + window.location.search;
      window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    }
    throw new Error('Unauthorized — redirecting to login');
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${detail || res.statusText}`);
  }
  return (await res.json()) as T;
}

export type TxRow = {
  id: number;
  occurred_at: string;
  card_alias: string;
  bank_code: string;
  raw_merchant_text: string;
  merchant_canonical: string | null;
  amount_original: number;
  currency: string;
  amount_vnd: number;
  type: 'purchase' | 'refund' | 'fee' | 'decline';
  status: string;
  has_alert: number;
  department_id: number | null;
  department_code: string | null;
  department_name: string | null;
};

export type TxList = { rows: TxRow[]; page: number; total_pages: number; total_count: number };

export type RetryResult = {
  processed: boolean;
  transaction_id?: number;
  alerts_count?: number;
  failure_reason?: string;
};

export type BulkOpResult = { ok: true; affected: number };

export type TxFilterShape = {
  date_from?: string;
  date_to?: string;
  card?: string;
  department?: string;
  type?: 'purchase' | 'refund' | 'fee' | 'decline';
  q?: string;
  has_alert?: string;
};

export type BulkAssignBody =
  | { mode: 'ids'; ids: number[]; department_id: number }
  | { mode: 'filter'; filter: TxFilterShape; department_id: number };

export type EditTxBody = {
  department_id?: number | null;
  raw_merchant_text?: string;
};

export type TxDetail = {
  transaction: TxRow & { fx_rate: number; raw_email_id: number };
  raw_email_body: string;
  alerts: { id: number; rule_code: string; severity: string; summary: string; status: string }[];
};

export type AdminUserRow = {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'sbu' | 'finance' | 'viewer';
  status: 'invited' | 'active' | 'disabled';
  ksystem_id: string | null;
  last_login_at: string | null;
  created_at: string;
  departments: { id: number; code: string; name: string }[];
};

export const api = {
  listTransactions(params: {
    page: number;
    limit?: number;
    card_id?: number;
    department?: number | 'unassigned';
    date_from?: string;
    date_to?: string;
    type?: 'purchase' | 'refund' | 'fee' | 'decline';
    q?: string;
    has_alert?: boolean;
    amount_min?: number;
    amount_max?: number;
    sort?: 'amount_desc';
    excludeDeclines?: boolean;
  }) {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.sort) qs.set('sort', params.sort);
    if (params.excludeDeclines) qs.set('exclude_declines', '1');
    if (params.card_id !== undefined) qs.set('card', String(params.card_id));
    if (params.department !== undefined) qs.set('department', String(params.department));
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.type) qs.set('type', params.type);
    if (params.q) qs.set('q', params.q);
    if (params.has_alert) qs.set('has_alert', '1');
    if (params.amount_min !== undefined) qs.set('amount_min', String(params.amount_min));
    if (params.amount_max !== undefined) qs.set('amount_max', String(params.amount_max));
    return apiFetch<TxList>(`/api/transactions?${qs.toString()}`);
  },
  listCardsLookup() {
    return apiFetch<{
      rows: { id: number; bank_code: string; last4: string; alias: string; status: string }[];
    }>(`/api/transactions/cards/lookup`);
  },
  getTransaction(id: number) {
    return apiFetch<TxDetail>(`/api/transactions/${id}`);
  },
  listFailedRawEmails(page: number) {
    return apiFetch<{
      rows: {
        id: number;
        gmail_message_id: string;
        received_at: string;
        from_addr: string;
        subject: string;
        body_text: string;
        parser_error: string;
        parsed_status: string;
      }[];
      total_pages: number;
      total_count: number;
    }>(`/api/raw-emails/failed?page=${page}`);
  },
  async retryRawEmail(id: number): Promise<RetryResult> {
    // The retry endpoint returns 200 on processed success and 422 when parse still fails.
    // We treat both as "retry executed" and surface the result to the caller; only
    // network errors / 5xx / 401 actually throw.
    const res = await fetch(`/api/raw-emails/${id}/retry`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.status === 401) {
      if (typeof window !== 'undefined') {
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
      }
      throw new Error('Unauthorized — redirecting to login');
    }
    if (res.status !== 200 && res.status !== 422) {
      const detail = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${detail || res.statusText}`);
    }
    return (await res.json()) as RetryResult;
  },
  skipRawEmail(id: number) {
    return apiFetch<{ ok: true }>(`/api/raw-emails/${id}/skip`, { method: 'POST' });
  },
  bulkAssignTransactions(body: BulkAssignBody) {
    return apiFetch<BulkOpResult>(`/api/transactions/bulk-assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  autoAssignTransactions() {
    return apiFetch<{ ok: true; assigned: number }>(`/api/transactions/auto-assign`, {
      method: 'POST',
    });
  },
  reorderDepartments(ids: number[]) {
    return apiFetch<{ ok: true; reordered: number }>(`/api/admin/departments/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  },
  reorderCards(ids: number[]) {
    return apiFetch<{ ok: true; reordered: number }>(`/api/admin/cards/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  },
  bulkAcknowledgeAlerts(ids: number[]) {
    return apiFetch<BulkOpResult>(`/api/alerts/bulk-acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  },
  bulkDismissAlerts(ids: number[]) {
    return apiFetch<BulkOpResult>(`/api/alerts/bulk-dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  },
  bulkSkipRawEmails(ids: number[]) {
    return apiFetch<BulkOpResult>(`/api/raw-emails/bulk-skip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  },
  bulkRetryRawEmails(ids: number[]) {
    return apiFetch<{ ok: true; queued: number }>(`/api/raw-emails/bulk-retry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  },
  manualCreate(id: number, body: unknown) {
    return apiFetch<{ ok: true; transaction_id: number }>(`/api/raw-emails/${id}/manual-create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  // ── admin: departments ───────────────────────────────────────────────────
  listDepartments() {
    return apiFetch<{
      rows: {
        id: number;
        code: string;
        name: string;
        budget_monthly_vnd: number | null;
        tx_count_this_month: number;
        spent_this_month_vnd: number;
        deleted_at: string | null;
      }[];
    }>(`/api/admin/departments`);
  },
  createDepartment(body: { code: string; name: string; budget_monthly_vnd?: number | null }) {
    return apiFetch<{ id: number }>(`/api/admin/departments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  updateDepartment(
    id: number,
    body: { code?: string; name?: string; budget_monthly_vnd?: number | null },
  ) {
    return apiFetch<{ ok: true }>(`/api/admin/departments/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  deleteDepartment(id: number) {
    return apiFetch<{ ok: true }>(`/api/admin/departments/${id}`, { method: 'DELETE' });
  },

  // ── admin: users ─────────────────────────────────────────────────────────
  listUsers(params: { status?: 'invited' | 'active' | 'disabled' } = {}) {
    const q = params.status ? `?status=${params.status}` : '';
    return apiFetch<{ rows: AdminUserRow[] }>(`/api/admin/users${q}`);
  },
  createUser(input: {
    email: string;
    name: string;
    role: 'admin' | 'sbu' | 'finance' | 'viewer';
    departmentIds: number[];
  }) {
    return apiFetch<{ id: number }>(`/api/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },
  fetchUser(id: number) {
    return apiFetch<AdminUserRow>(`/api/admin/users/${id}`);
  },
  updateUser(
    id: number,
    patch: {
      name?: string;
      role?: 'admin' | 'sbu' | 'finance' | 'viewer';
      status?: 'active' | 'disabled';
      departmentIds?: number[];
    },
  ) {
    return apiFetch<{ ok: true }>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
  },
  deleteUser(id: number) {
    return apiFetch<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' });
  },

  // ── admin: cards ────────────────────────────────────────────────────────
  listCards() {
    return apiFetch<{
      rows: {
        id: number;
        bank_code: string;
        bank_name: string;
        last4: string;
        alias: string;
        status: 'active' | 'frozen' | 'closed';
        monthly_limit_vnd: number | null;
        owning_department_id: number | null;
        owning_department_code: string | null;
        owning_department_name: string | null;
        tx_count_this_month: number;
      }[];
    }>(`/api/admin/cards`);
  },
  createCard(body: {
    bank_code: string;
    last4: string;
    alias: string;
    monthly_limit_vnd?: number | null;
    owning_department_id?: number | null;
  }) {
    return apiFetch<{ id: number }>(`/api/admin/cards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  updateCard(
    id: number,
    body: {
      alias?: string;
      status?: 'active' | 'frozen' | 'closed';
      monthly_limit_vnd?: number | null;
      owning_department_id?: number | null;
    },
  ) {
    return apiFetch<{ ok: true }>(`/api/admin/cards/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  deleteCard(id: number) {
    return apiFetch<{ ok: true }>(`/api/admin/cards/${id}`, { method: 'DELETE' });
  },

  // ── pending + assign ────────────────────────────────────────────────────
  listPending(params: {
    page: number;
    card_id?: number;
    date_from?: string;
    date_to?: string;
    type?: 'purchase' | 'refund' | 'fee' | 'decline';
    q?: string;
  }) {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page));
    if (params.card_id !== undefined) qs.set('card', String(params.card_id));
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.type) qs.set('type', params.type);
    if (params.q) qs.set('q', params.q);
    return apiFetch<TxList>(`/api/transactions/pending?${qs.toString()}`);
  },
  getSuggestions(id: number) {
    return apiFetch<{
      suggestions: {
        department_id: number;
        department_code: string;
        department_name: string;
        confidence: number;
      }[];
    }>(`/api/transactions/${id}/suggestions`);
  },
  assignTransaction(id: number, department_id: number) {
    return apiFetch<{ ok: true }>(`/api/transactions/${id}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ department_id }),
    });
  },
  unassignTransaction(id: number) {
    return apiFetch<{ ok: true }>(`/api/transactions/${id}/unassign`, { method: 'POST' });
  },
  editTransaction(id: number, body: EditTxBody) {
    return apiFetch<{ ok: true }>(`/api/transactions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  // ── comments ────────────────────────────────────────────────────────────
  listComments(id: number) {
    return apiFetch<{
      comments: {
        id: number;
        transaction_id: number;
        body_text: string;
        actor_email: string;
        created_at: string;
      }[];
    }>(`/api/transactions/${id}/comments`);
  },
  createComment(id: number, body_text: string) {
    return apiFetch<{ id: number }>(`/api/transactions/${id}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body_text }),
    });
  },

  // ── attachments ──────────────────────────────────────────────────────────
  listAttachments(id: number) {
    return apiFetch<{
      attachments: {
        id: number;
        transaction_id: number;
        original_filename: string;
        mime_type: string;
        size_bytes: number;
        uploader_email: string;
        created_at: string;
      }[];
    }>(`/api/transactions/${id}/attachments`);
  },
  uploadAttachment(id: number, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return apiFetch<{
      id: number;
      original_filename: string;
      mime_type: string;
      size_bytes: number;
      created_at: string;
    }>(`/api/transactions/${id}/attachments`, { method: 'POST', body: fd });
  },
  deleteAttachment(id: number) {
    return apiFetch<{ ok: true }>(`/api/attachments/${id}`, { method: 'DELETE' });
  },

  // ── alerts ───────────────────────────────────────────────────────────────
  listAlerts(params: {
    severity?: string;
    rule_code?: string;
    department?: string;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params.severity) qs.set('severity', params.severity);
    if (params.rule_code) qs.set('rule_code', params.rule_code);
    if (params.department) qs.set('department', params.department);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    return apiFetch<{
      rows: {
        id: number;
        transaction_id: number;
        rule_code: string;
        severity: 'low' | 'medium' | 'high';
        summary: string;
        status: 'open' | 'acknowledged' | 'dismissed';
        acknowledged_at: string | null;
        acknowledged_by_email: string | null;
        notified_at: string | null;
        created_at: string;
        card_alias: string;
        bank_code: string;
        merchant_canonical: string | null;
        amount_vnd: number;
        occurred_at: string;
      }[];
      total_count: number;
    }>(`/api/alerts${qs.toString() ? '?' + qs.toString() : ''}`);
  },
  countAlerts() {
    return apiFetch<{ open_count: number; high_count: number }>(`/api/alerts/count`);
  },
  acknowledgeAlert(id: number) {
    return apiFetch<{ ok: true }>(`/api/alerts/${id}/acknowledge`, { method: 'POST' });
  },
  dismissAlert(id: number) {
    return apiFetch<{ ok: true }>(`/api/alerts/${id}/dismiss`, { method: 'POST' });
  },

  // ── dashboard ────────────────────────────────────────────────────────────
  getKpis(
    params: { department?: string; date_from?: string; date_to?: string; all?: boolean } = {},
  ) {
    const qs = new URLSearchParams();
    if (params.department) qs.set('department', params.department);
    if (params.all) {
      qs.set('range', 'all');
    } else {
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
    }
    const q = qs.toString();
    return apiFetch<{
      total_vnd: number;
      prev_total_vnd: number;
      tx_count: number;
      prev_tx_count: number;
      pending_count: number;
      prev_pending_count: number;
      high_count: number;
      prev_high_count: number;
    }>(`/api/dashboard/kpis${q ? `?${q}` : ''}`);
  },
  getMonthly(months = 6, department?: string) {
    const qs = new URLSearchParams();
    qs.set('months', String(months));
    if (department) qs.set('department', department);
    return apiFetch<{ rows: { month: string; total_vnd: number }[] }>(
      `/api/dashboard/monthly?${qs.toString()}`,
    );
  },
  getByDepartment(params: { date_from?: string; date_to?: string; all?: boolean } = {}) {
    const qs = new URLSearchParams();
    if (params.all) {
      qs.set('range', 'all');
    } else {
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
    }
    const q = qs.toString();
    return apiFetch<{
      rows: { department_code: string; department_name: string; total_vnd: number }[];
      total_vnd: number;
    }>(`/api/dashboard/by-department${q ? `?${q}` : ''}`);
  },
  getAlertHeatmap(days = 30, department?: string) {
    const qs = new URLSearchParams();
    qs.set('days', String(days));
    if (department) qs.set('department', department);
    return apiFetch<{
      rows: { date: string; open_count: number; max_severity: 'high' | 'medium' | 'low' | null }[];
    }>(`/api/dashboard/alert-heatmap?${qs.toString()}`);
  },
  // ── admin: statement reconciliation ─────────────────────────────────────
  listStatements() {
    return apiFetch<{
      rows: {
        id: number;
        bank_id: number;
        bank_code: string;
        period_year_month: string;
        uploaded_by_email: string | null;
        uploaded_at: string;
        total: number;
        matched: number;
        missing_in_email: number;
      }[];
    }>(`/api/admin/statements`);
  },
  async uploadStatement(input: { file: File; bank_code: string; period: string }) {
    const fd = new FormData();
    fd.set('file', input.file);
    fd.set('bank_code', input.bank_code);
    fd.set('period', input.period);
    const res = await fetch(`/api/admin/statements`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (res.status === 401) {
      if (typeof window !== 'undefined') {
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
      }
      throw new Error('Unauthorized — redirecting to login');
    }
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${detail || res.statusText}`);
    }
    return (await res.json()) as {
      statement_id: number;
      rows: { total: number; matched: number; missing_in_email: number; parse_errors: number };
    };
  },
  reconcileStatement(id: number) {
    return apiFetch<{ ok: true; total: number; matched: number; missing_in_email: number }>(
      `/api/admin/statements/${id}/reconcile`,
      { method: 'POST' },
    );
  },
  deleteStatement(id: number) {
    return apiFetch<{ ok: true }>(`/api/admin/statements/${id}`, { method: 'DELETE' });
  },

  getTopMerchants(
    params: { department?: string; date_from?: string; date_to?: string; all?: boolean } = {},
  ) {
    const qs = new URLSearchParams();
    if (params.department) qs.set('department', params.department);
    if (params.all) {
      qs.set('range', 'all');
    } else {
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
    }
    const q = qs.toString();
    return apiFetch<{
      rows: { merchant: string; tx_count: number; total_vnd: number }[];
    }>(`/api/dashboard/top-merchants${q ? `?${q}` : ''}`);
  },
};
